import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { getPaymentStatus } from '../utils/api';
import AgentLayout from '../components/AgentLayout';

const BillingSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [details, setDetails] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const token = localStorage.getItem('agentToken');
    if (!sessionId || !token) { navigate('/agent/billing'); return; }
    pollStatus(sessionId, token, 0);
  }, [searchParams, navigate]);

  const pollStatus = async (sessionId, token, attempt) => {
    if (attempt >= 5) {
      setStatus('timeout');
      return;
    }
    try {
      const result = await getPaymentStatus(sessionId, token);
      if (result.paymentStatus === 'paid') {
        setStatus('success');
        setDetails(result);
        return;
      }
      if (result.status === 'expired') {
        setStatus('expired');
        return;
      }
      // Keep polling
      setTimeout(() => pollStatus(sessionId, token, attempt + 1), 2000);
    } catch (e) {
      if (attempt < 4) setTimeout(() => pollStatus(sessionId, token, attempt + 1), 2000);
      else setStatus('error');
    }
  };

  return (
    <AgentLayout title="Payment Status">
      <div className="max-w-md mx-auto text-center py-12" data-testid="billing-success-page">
        {status === 'checking' && (
          <>
            <Loader2 size={48} className="animate-spin text-brand-blue mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment...</h2>
            <p className="text-gray-500">Please wait while we confirm your payment.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="payment-success-title">Payment Successful!</h2>
            <p className="text-gray-500 mb-2">Your training package <strong>{details?.metadata?.package_name}</strong> is now active.</p>
            <p className="text-sm text-gray-400 mb-6">Amount: ${(details?.amount / 100)?.toFixed(2)} {details?.currency?.toUpperCase()}</p>
            <button onClick={() => navigate('/agent/billing')} className="px-6 py-3 bg-brand-blue text-white rounded-lg font-semibold hover:bg-brand-orange transition" data-testid="back-to-billing-btn">
              Back to Training
            </button>
          </>
        )}
        {(status === 'expired' || status === 'error' || status === 'timeout') && (
          <>
            <h2 className="text-2xl font-bold text-red-600 mb-2">Payment Issue</h2>
            <p className="text-gray-500 mb-6">
              {status === 'expired' ? 'Your payment session expired.' : 'We could not verify your payment. Please check your email for confirmation.'}
            </p>
            <button onClick={() => navigate('/agent/billing')} className="px-6 py-3 bg-brand-blue text-white rounded-lg font-semibold hover:bg-brand-orange transition">
              Try Again
            </button>
          </>
        )}
      </div>
    </AgentLayout>
  );
};

export default BillingSuccess;
